/**
 * `CAMPO_ESCOPO` — onde mora a chave de escopo de cada recurso, e em que
 * dimensão ela vive (T59, `ADR-0004`). Movido para fora de `escopo.ts` na
 * T143, e por um motivo estrutural, não de organização: `compilador.ts`
 * (T132, DEL-01) precisa ler este mapa para restringir `escopo.obras` de uma
 * delegação por campo de obra, e `escopo.ts` já importa (como tipo)
 * `RegraCompilada` de `compilador.ts` — as duas coisas no mesmo arquivo
 * formariam um ciclo de import de VALOR (`compilador.ts` → `escopo.ts` →
 * `compilador.ts`), que `gate:fronteira-dominio` mede e reprova. Isolar o
 * mapa aqui, num arquivo que não importa nada de `compilador.ts` nem de
 * `escopo.ts`, quebra o ciclo pela raiz. `escopo.ts` continua reexportando
 * `CAMPO_ESCOPO`/`Dimensao`, então nenhum importador existente muda.
 */
import type { Recurso } from "./vocabulario";

/**
 * As duas dimensões de escopo.
 *
 * `empreendimento` é obra **ou** fabricação — entidades irmãs, não hierárquicas
 * (decisão 9 do time), com id no mesmo namespace (E1). `proposta` é a terceira,
 * criada pela decisão de 2026-09-03 para `TAF`.
 */
export type Dimensao = "empreendimento" | "proposta";

/**
 * Transcrito de `docs/contratos-backend.md` §10.2, que é a forma final decidida.
 * Recurso **fora** desta tabela não é "sem filtro": para `tipo: "cliente"` ele
 * **nega** — invariante **E4**, que inverte o `if (!campo) return r` do desenho
 * antigo, o qual liberava.
 *
 * Duas correções ao `ADR-0004` §D2 estão embutidas aqui, as duas de 2026-09-03:
 *
 * - **`PROJETO_FABRICA` é pendurado em OBRA**, não em fabricação.
 *   `ProjetoFabricaModel.ts:23` declara `OBRA` e `NOME_OBRA` separados — o id
 *   existe e é distinto do nome (§10.1).
 * - **`TAF` escopa por `proposta`**, dimensão própria.
 *
 * Os nove recursos de equipe interna (`TicketSgi`, `POSVENDAS`,
 * `TicketInteracao`, `ProcessoJuridico`, `Permissao`, `OS_FIELD`,
 * `OS_AUTOMACAO`, `PEDIDO_AUTOMACAO`, `CONTRATO_AUTOMACAO`) e a exceção
 * declarada `Diretorio` (ACL na linha, `ADR-0003`) ficam de fora — e é por isso
 * que, para um cliente, eles negam.
 */
export const CAMPO_ESCOPO: Partial<Record<Recurso, { campo: string; dimensao: Dimensao }>> = {
  Obra: { campo: "Id_Lista", dimensao: "empreendimento" },
  FABRICACAO: { campo: "ID", dimensao: "empreendimento" },
  RDO: { campo: "obra", dimensao: "empreendimento" },
  // Corrigido de `"Obra"` para `"Codigo_obra"` em 2026-09-08, por decisao do
  // usuario (T60). A classe `views/Materiais/Materiais.ts` (movida por R1 na
  // T83; era `classes/materiais/Materiais.ts`) declara `Codigo_obra`, e nunca
  // `Obra` — o mesmo nome que `MateriaisApi.ts` e os tres formularios usam.
  // Com o campo errado o `$in` nunca casava, entao cliente com
  // escopo de obra via ZERO materiais; agora ve os daquela obra. A tabela do
  // §10.2 do contrato transcrevia `Obra` sem ter confrontado o schema real, que
  // e um passo distinto de transcrever — foi o que o §10.1 fez para
  // `PROJETO_FABRICA` e nao foi feito aqui.
  Materiais: { campo: "Codigo_obra", dimensao: "empreendimento" },
  PROJETO: { campo: "Obra", dimensao: "empreendimento" },
  PROJETO_FABRICA: { campo: "OBRA", dimensao: "empreendimento" },
  Contratos: { campo: "obra", dimensao: "empreendimento" },
  SEGUROS: { campo: "Obra", dimensao: "empreendimento" },
  Produto: { campo: "fabricacao", dimensao: "empreendimento" },
  TAF: { campo: "proposta", dimensao: "proposta" },
};
